import * as React from 'react';
import {
    Box, Card, CardContent, Typography, Grid, Avatar, Chip,
    CircularProgress, Alert, Accordion, AccordionSummary,
    AccordionDetails, Badge, Divider, IconButton, Tooltip
} from '@mui/material';
import { useNotify } from 'react-admin';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import SportsIcon from '@mui/icons-material/Sports';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ShieldIcon from '@mui/icons-material/Shield';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import RefreshIcon from '@mui/icons-material/Refresh';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

// Icone per i ruoli
const getRoleIcon = (ruoloId) => {
    switch (ruoloId) {
        case 1: return <ShieldIcon />; // PORTIERE
        case 2: return <GpsFixedIcon />; // DIFENSORE
        case 3: return <DirectionsRunIcon />; // CENTROCAMPISTA
        case 4: return <SportsSoccerIcon />; // ATTACCANTE
        default: return <PersonIcon />;
    }
};

// Colori per i ruoli
const getRoleColor = (ruoloId) => {
    switch (ruoloId) {
        case 1: return { main: '#ff9800', light: '#fff3e0' }; // PORTIERE - arancione
        case 2: return { main: '#2196f3', light: '#e3f2fd' }; // DIFENSORE - blu
        case 3: return { main: '#4caf50', light: '#e8f5e8' }; // CENTROCAMPISTA - verde
        case 4: return { main: '#f44336', light: '#ffebee' }; // ATTACCANTE - rosso
        default: return { main: '#9e9e9e', light: '#f5f5f5' };
    }
};

export const TeamPlayers = ({ teamId }) => {
    const notify = useNotify();

    // Stati
    const [players, setPlayers] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [groupedPlayers, setGroupedPlayers] = React.useState({});

    // Carica i giocatori della squadra
    const loadPlayers = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/proc-v-player`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: { teamId: parseInt(teamId) }
                }
            );

            const data = response.data;
            if (data.data) {
                setPlayers(data.data);

                // Raggruppa per ruolo
                const grouped = data.data.reduce((acc, player) => {
                    const role = player.nomeRuolo;
                    if (!acc[role]) {
                        acc[role] = {
                            players: [],
                            ruoloId: player.ruoloId,
                            count: 0
                        };
                    }
                    acc[role].players.push(player);
                    acc[role].count++;
                    return acc;
                }, {});

                setGroupedPlayers(grouped);
            }

        } catch (error) {
            console.error("Errore nel recupero giocatori:", error);
            setError("Errore nel caricamento dei giocatori");
            notify("Errore nel caricamento dei giocatori", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica i dati al mount e quando cambia teamId
    React.useEffect(() => {
        if (teamId) {
            loadPlayers();
        }
    }, [teamId]);

    // Calcola lo stato del giocatore
    const getPlayerStatus = (player) => {
        if (player.flAnnullato) {
            return { label: 'Annullato', color: '#f44336', bgcolor: '#ffebee' };
        } else if (!player.flAttivo) {
            return { label: 'Inattivo', color: '#ff9800', bgcolor: '#fff3e0' };
        } else if (!player.flVisibile) {
            return { label: 'Nascosto', color: '#9e9e9e', bgcolor: '#f5f5f5' };
        } else {
            return { label: 'Attivo', color: '#4caf50', bgcolor: '#e8f5e8' };
        }
    };

    // Loading state
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    Caricamento giocatori...
                </Typography>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert
                severity="error"
                sx={{ m: 2 }}
                action={
                    <IconButton color="inherit" size="small" onClick={loadPlayers}>
                        <RefreshIcon />
                    </IconButton>
                }
            >
                {error}
            </Alert>
        );
    }

    // No players found
    if (players.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    Nessun giocatore trovato
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                    Non ci sono giocatori associati a questa squadra
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header con statistiche */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        Giocatori della Squadra
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Totale: {players.length} giocatori
                    </Typography>
                </Box>

                <Tooltip title="Aggiorna lista">
                    <IconButton onClick={loadPlayers} color="primary">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Statistiche per ruolo */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {Object.entries(groupedPlayers).map(([roleName, roleData]) => {
                    const roleColor = getRoleColor(roleData.ruoloId);
                    return (
                        <Grid item xs={6} md={3} key={roleName}>
                            <Card sx={{
                                textAlign: 'center',
                                bgcolor: roleColor.light,
                                border: `1px solid ${roleColor.main}20`
                            }}>
                                <CardContent sx={{ py: 2 }}>
                                    <Box sx={{ color: roleColor.main, mb: 1 }}>
                                        {getRoleIcon(roleData.ruoloId)}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: roleColor.main }}>
                                        {roleData.count}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {roleName}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Lista giocatori raggruppata per ruolo */}
            <Box sx={{ space: 2 }}>
                {Object.entries(groupedPlayers).map(([roleName, roleData], roleIndex) => {
                    const roleColor = getRoleColor(roleData.ruoloId);

                    return (
                        <motion.div
                            key={roleName}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: roleIndex * 0.1 }}
                        >
                            <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        bgcolor: roleColor.light,
                                        borderLeft: `4px solid ${roleColor.main}`,
                                        '&:hover': { bgcolor: `${roleColor.main}10` }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                        <Box sx={{ color: roleColor.main }}>
                                            {getRoleIcon(roleData.ruoloId)}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {roleName}
                                        </Typography>
                                        <Badge
                                            badgeContent={roleData.count}
                                            color="primary"
                                            sx={{ ml: 'auto' }}
                                        />
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails sx={{ p: 0 }}>
                                    <Grid container>
                                        {roleData.players.map((player, playerIndex) => {
                                            const status = getPlayerStatus(player);

                                            return (
                                                <Grid item xs={12} md={6} lg={4} key={player.playerId}>
                                                    <motion.div
                                                        initial={{ opacity: 0, scale: 0.95 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: playerIndex * 0.05 }}
                                                    >
                                                        <Card
                                                            sx={{
                                                                m: 1,
                                                                borderRadius: 2,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: 3
                                                                }
                                                            }}
                                                        >
                                                            <CardContent sx={{ p: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                                    {/* Avatar */}
                                                                    <Avatar
                                                                        src={player.foto}
                                                                        alt={player.nomeGiocatore}
                                                                        sx={{
                                                                            width: 50,
                                                                            height: 50,
                                                                            bgcolor: player.foto ? 'transparent' : roleColor.main,
                                                                            fontSize: '1.2rem'
                                                                        }}
                                                                    >
                                                                        {!player.foto && player.nomeGiocatore ?
                                                                            player.nomeGiocatore.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                                                                            '👤'
                                                                        }
                                                                    </Avatar>

                                                                    {/* Informazioni giocatore */}
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography
                                                                            variant="subtitle1"
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                                fontSize: '0.95rem',
                                                                                lineHeight: 1.2,
                                                                                mb: 0.5
                                                                            }}
                                                                            noWrap
                                                                        >
                                                                            {player.nomeGiocatore}
                                                                        </Typography>

                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            sx={{ fontSize: '0.8rem', mb: 1 }}
                                                                        >
                                                                            ID: {player.playerId}
                                                                        </Typography>

                                                                        {/* Status */}
                                                                        <Chip
                                                                            label={status.label}
                                                                            size="small"
                                                                            sx={{
                                                                                color: status.color,
                                                                                bgcolor: status.bgcolor,
                                                                                fontSize: '0.7rem',
                                                                                height: 20
                                                                            }}
                                                                        />

                                                                        {/* Note se presenti */}
                                                                        {player.note && player.note.trim() && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    mt: 1,
                                                                                    fontStyle: 'italic',
                                                                                    fontSize: '0.75rem',
                                                                                    color: 'text.secondary'
                                                                                }}
                                                                            >
                                                                                {player.note}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>

                                                                    {/* Icona visibilità */}
                                                                    <Box>
                                                                        {player.flVisibile ?
                                                                            <VisibilityIcon sx={{ fontSize: 16, color: 'success.main' }} /> :
                                                                            <VisibilityOffIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                            </CardContent>
                                                        </Card>
                                                    </motion.div>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </motion.div>
                    );
                })}
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                </Typography>
            </Box>
        </Box>
    );
};